import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Card from '../../../components/common/Card';
import Badge from '../../../components/common/Badge';
import Alert from '../../../components/common/Alert';
import MetricCard from '../../../components/common/MetricCard';
import { Building2, Users, Mail, CheckCircle } from 'lucide-react';
import { useOrganization } from '../../../hooks/useOrganization';
import { supabase } from '../../../config/supabase';

const EMPLOYEE_RANGES = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+'
];

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Retail',
  'Other'
];

export default function OrganizationSettings() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const [error, setError] = useState(null);

  const { data: orgData, isLoading } = useQuery({
    queryKey: ['organizationSettings', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_dashboard_view')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!organizationId
  });

  const updateMutation = useMutation({
    mutationFn: async (updates) => {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', organizationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['organizationSettings']);
      setError(null);
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const handleUpdate = async (field, value) => {
    updateMutation.mutate({ [field]: value });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          Organization Settings
        </h1>
      </div>

      {error && (
        <Alert type="error">
          {error}
        </Alert>
      )}

      {/* Organization Details */}
      <Card title="Organization Details">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Organization Name
            </label>
            <input
              type="text"
              value={orgData?.name || ''}
              onChange={(e) => handleUpdate('name', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Industry
            </label>
            <select
              value={orgData?.industry || ''}
              onChange={(e) => handleUpdate('industry', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            >
              <option value="">Select Industry</option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Employee Count Range
            </label>
            <select
              value={orgData?.employee_count_range || ''}
              onChange={(e) => handleUpdate('employee_count_range', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring focus:ring-indigo-200"
            >
              <option value="">Select Range</option>
              {EMPLOYEE_RANGES.map((range) => (
                <option key={range} value={range}>
                  {range} employees
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Domain Name
            </label>
            <input
              type="text"
              value={orgData?.domain_name || ''}
              readOnly
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50"
            />
            <Badge 
              variant={orgData?.domain_status === 'verified' ? 'success' : 'warning'}
              className="mt-2"
            >
              {orgData?.domain_status === 'verified' ? 'Verified' : 'Pending Verification'}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Active Users"
          icon={<Users className="h-5 w-5" />}
          value={orgData?.active_users || 0}
        />

        <MetricCard
          title="Total Emails"
          icon={<Mail className="h-5 w-5" />}
          value={orgData?.total_emails || 0}
        />

        <MetricCard
          title="Verified Emails"
          icon={<CheckCircle className="h-5 w-5" />}
          value={orgData?.verified_emails || 0}
        />
      </div>
    </div>
  );
}